"use client";

import StandardCard from "../common/StandardCard";
import { ButtonIcon } from "@/components/common/StandardButton";
import { ExternalLink, Loader2, ShoppingBag } from "lucide-react";
import { useEffect, useState, useRef } from "react";

interface RecentOrder {
  id: string;
  orderNumber: number;
  date: Date;
  amount: number;
}

interface RecentOrdersCardProps {
  selectedOrder: any;
  storeId: string;
}

const RecentOrdersCard = ({
  selectedOrder,
  storeId,
}: RecentOrdersCardProps) => {
  const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const hasFetchedRef = useRef<string | null>(null);

  useEffect(() => {
    const customerId = selectedOrder?.customer?.user_id || selectedOrder?.user_id;
    
    if (!customerId) {
      console.log('[RecentOrdersCard] No customerId, skipping fetch');
      return;
    }

    // Skip if already loading
    if (loading) {
      console.log('[RecentOrdersCard] Already loading, skipping fetch');
      return;
    }

    // Skip if we already fetched for this customer
    if (hasFetchedRef.current === customerId) {
      console.log('[RecentOrdersCard] Already fetched orders for customer:', customerId);
      return;
    }

    console.log('[RecentOrdersCard] Fetching recent orders for customer:', customerId);
    hasFetchedRef.current = customerId;
    fetchRecentOrders();
  }, [selectedOrder?.customer?.user_id, selectedOrder?.user_id]);

  const fetchRecentOrders = async () => {
    const customerId =
      selectedOrder?.customer?.user_id || selectedOrder?.user_id;
    if (!customerId || !storeId) return;

    setLoading(true);
    try {
      // Fetch recent orders for this customer
      const response = await fetch(
        `/api/v1/admin/${storeId}/order?customer_id=${customerId}&limit=5&sort=created_at&order=desc`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch recent orders");
      }

      const data = await response.json();

      if (data.valid && data.data) {
        const transformedOrders: RecentOrder[] = data.data.map(
          (order: any) => ({
            id: order.id || order._id,
            orderNumber: order.order_number || 0,
            date: new Date(order.created_at || Date.now()),
            amount:
              order.calculation?.grand_total_int || order.total_price_int || 0,
          }),
        );

        setRecentOrders(transformedOrders);
      }
    } catch (error) {
      console.error("Error fetching recent orders:", error);
      // Fallback to current order only
      const fallbackOrder: RecentOrder = {
        id: selectedOrder.id,
        orderNumber: selectedOrder.order_number || 1,
        date: new Date(selectedOrder.created_at || Date.now()),
        amount: selectedOrder.calculation?.grand_total_int || 0,
      };
      setRecentOrders([fallbackOrder]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <StandardCard
      title="Recent Orders"
      headerAction={
        <ButtonIcon variant="ghost">
          <ExternalLink className="h-4 w-4" />
        </ButtonIcon>
      }
    >
      <div className="space-y-2">
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">
                Loading recent orders...
              </span>
            </div>
          </div>
        ) : recentOrders.length === 0 ? (
          <div className="text-center py-6">
            <ShoppingBag className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              No recent orders found
            </p>
          </div>
        ) : (
          <>
            {recentOrders.map((order) => (
              <div
                key={order.id}
                className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50"
              >
                <div>
                  <p className="text-sm font-medium text-foreground">
                    #{order.orderNumber}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {order.date.toLocaleDateString()} | Amount: ₹
                    {(order.amount / 100).toFixed(2)}
                  </p>
                </div>
                <ButtonIcon variant="ghost">
                  <ExternalLink className="h-3 w-3" />
                </ButtonIcon>
              </div>
            ))}

            {recentOrders.length > 2 && (
              <div className="text-center">
                <p className="text-xs text-muted-foreground">
                  Showing latest {recentOrders.length} orders
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </StandardCard>
  );
};

export default RecentOrdersCard;
